import { formatDate } from '../config/appConfig';

export interface Notification {
  id: number;
  userId: number;
  entityType: string;
  entityId: number;
  message: string;
  link?: string;
  createdAt: string;
  readAt?: string;
}

export type NotificationType = 'todo' | 'email' | 'appointment' | 'client' | 'other';

export interface NotificationTypeConfig {
  icon: string;
  color: string;
  label: string;
}

export const NOTIFICATION_TYPES: Record<NotificationType, NotificationTypeConfig> = {
  todo: {
    icon: 'check-circle',
    color: 'text-blue-600',
    label: 'Todo',
  },
  email: {
    icon: 'mail',
    color: 'text-sky-600',
    label: 'Email',
  },
  appointment: {
    icon: 'calendar',
    color: 'text-green-600',
    label: 'Appointment',
  },
  client: {
    icon: 'user-group',
    color: 'text-indigo-600',
    label: 'Organization',
  },
  other: {
    icon: 'bell',
    color: 'text-gray-600',
    label: 'Notification',
  },
};

export function getNotificationType(entityType: string): NotificationType {
  const lowerType = entityType.toLowerCase();
  if (lowerType.includes('email_mention') || lowerType.includes('email_assigned')) return 'email';
  if (lowerType.includes('todo')) return 'todo';
  if (lowerType.includes('email')) return 'email';
  if (lowerType.includes('appointment')) return 'appointment';
  if (lowerType.includes('client')) return 'client';
  return 'other';
}

export function formatNotificationDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;

  return formatDate(date);
}

export function groupNotificationsByDate(notifications: Notification[]): {
  today: Notification[];
  yesterday: Notification[];
  thisWeek: Notification[];
  older: Notification[];
} {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const weekAgo = new Date(today);
  weekAgo.setDate(weekAgo.getDate() - 7);

  const groups = {
    today: [] as Notification[],
    yesterday: [] as Notification[],
    thisWeek: [] as Notification[],
    older: [] as Notification[],
  };

  notifications.forEach((notification) => {
    const notifDate = new Date(notification.createdAt);
    if (notifDate >= today) {
      groups.today.push(notification);
    } else if (notifDate >= yesterday) {
      groups.yesterday.push(notification);
    } else if (notifDate >= weekAgo) {
      groups.thisWeek.push(notification);
    } else {
      groups.older.push(notification);
    }
  });

  return groups;
}
